export type ToastType = 'info' | 'success' | 'error';

export function toast(message: string, type: ToastType = 'info') {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('ui:toast', { detail: { message, type } }));
}

export type ConfirmOptions = {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
};

export function askConfirm(opts: ConfirmOptions = {}): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    window.dispatchEvent(new CustomEvent('ui:confirm', { detail: { opts, resolve } }));
  });
}

// Copy text to the clipboard, with a fallback for environments where the async
// Clipboard API is blocked (notably the Android WebView). Returns whether it
// succeeded so callers can surface feedback.
export async function copyToClipboard(text: string): Promise<boolean> {
  const value = String(text ?? '');
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && (typeof window === 'undefined' || window.isSecureContext)) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch { /* fall through to the legacy path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try { ta.setSelectionRange(0, value.length); } catch { /* noop */ }
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export type PromptOptions = {
  title?: string;
  label?: string;
  placeholder?: string;
  initialValue?: string;
  okText?: string;
  cancelText?: string;
  textarea?: boolean;
};

// In-app text prompt. Use instead of window.prompt(), which the desktop (.exe)
// Electron renderer does not implement (it returns null). Resolves to the entered
// value, or null if cancelled.
export function askPrompt(opts: PromptOptions = {}): Promise<string | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  return new Promise<string | null>((resolve) => {
    window.dispatchEvent(new CustomEvent('ui:prompt', { detail: { opts, resolve } }));
  });
}

